/**
 * The pre-season chapter's screen guides and guided tours — ADR 9044.
 *
 * One entry per route of `./manifest.ts`, keyed by its `path` exactly as declared, in the
 * manifest's order. `tools/screen-guides.mjs` fails `census:features` on a route without an entry,
 * a key unwritten in any locale, or a tour anchor no element carries; `../guides.test.ts` checks
 * each anchor is carried by the screen its step belongs to.
 *
 * The texts (`locale/<locale>/preseason.json`, `preseason.guide.*`) were written from each screen's
 * code — what it reads, what each figure means, what each state says and why — not from its
 * comments. A screen change that changes what a planner sees changes its guide in the same PR.
 *
 * The chapter is still `disabled: true` in `./manifest.ts`, so the rail draws its screens inert and
 * they are reached by a direct link or the chapter bar on each page; the guides describe the screens
 * as they behave, including the states they show while nothing is published for them.
 */

import type { GuideTable } from '../guides';

export const PRESEASON_GUIDES: GuideTable = {
  'otb': {
    purposeKey: 'preseason.guide.otb.purpose',
    readingKey: 'preseason.guide.otb.reading',
    actionsKey: 'preseason.guide.otb.actions',
    statesKey: 'preseason.guide.otb.states',
    sourceKey: 'preseason.guide.otb.source',
    tour: [
      { anchor: 'preseason-chapter-bar', titleKey: 'preseason.guide.otb.tour.chapter.title', bodyKey: 'preseason.guide.otb.tour.chapter.body' },
      { anchor: 'page-status', titleKey: 'preseason.guide.otb.tour.state.title', bodyKey: 'preseason.guide.otb.tour.state.body' },
      { anchor: 'preseason-otb-kpis', titleKey: 'preseason.guide.otb.tour.figures.title', bodyKey: 'preseason.guide.otb.tour.figures.body' },
      { anchor: 'preseason-otb-table', titleKey: 'preseason.guide.otb.tour.table.title', bodyKey: 'preseason.guide.otb.tour.table.body' },
      { anchor: 'preseason-otb-inseason', titleKey: 'preseason.guide.otb.tour.inseason.title', bodyKey: 'preseason.guide.otb.tour.inseason.body' },
    ],
  },
  'clusters': {
    purposeKey: 'preseason.guide.clusters.purpose',
    readingKey: 'preseason.guide.clusters.reading',
    actionsKey: 'preseason.guide.clusters.actions',
    statesKey: 'preseason.guide.clusters.states',
    sourceKey: 'preseason.guide.clusters.source',
    tour: [
      { anchor: 'page-status', titleKey: 'preseason.guide.clusters.tour.state.title', bodyKey: 'preseason.guide.clusters.tour.state.body' },
      { anchor: 'preseason-context', titleKey: 'preseason.guide.clusters.tour.context.title', bodyKey: 'preseason.guide.clusters.tour.context.body' },
      { anchor: 'preseason-clusters-kpis', titleKey: 'preseason.guide.clusters.tour.figures.title', bodyKey: 'preseason.guide.clusters.tour.figures.body' },
      { anchor: 'preseason-clusters-shape', titleKey: 'preseason.guide.clusters.tour.shape.title', bodyKey: 'preseason.guide.clusters.tour.shape.body' },
      { anchor: 'preseason-clusters-schemes', titleKey: 'preseason.guide.clusters.tour.schemes.title', bodyKey: 'preseason.guide.clusters.tour.schemes.body' },
      { anchor: 'preseason-clusters-membership', titleKey: 'preseason.guide.clusters.tour.membership.title', bodyKey: 'preseason.guide.clusters.tour.membership.body' },
      { anchor: 'preseason-clusters-doors', titleKey: 'preseason.guide.clusters.tour.doors.title', bodyKey: 'preseason.guide.clusters.tour.doors.body' },
      { anchor: 'preseason-clusters-localisation', titleKey: 'preseason.guide.clusters.tour.localisation.title', bodyKey: 'preseason.guide.clusters.tour.localisation.body' },
    ],
  },
  'size-curves': {
    purposeKey: 'preseason.guide.sizeCurves.purpose',
    readingKey: 'preseason.guide.sizeCurves.reading',
    actionsKey: 'preseason.guide.sizeCurves.actions',
    statesKey: 'preseason.guide.sizeCurves.states',
    sourceKey: 'preseason.guide.sizeCurves.source',
    tour: [
      { anchor: 'page-status', titleKey: 'preseason.guide.sizeCurves.tour.state.title', bodyKey: 'preseason.guide.sizeCurves.tour.state.body' },
      { anchor: 'preseason-context', titleKey: 'preseason.guide.sizeCurves.tour.context.title', bodyKey: 'preseason.guide.sizeCurves.tour.context.body' },
      { anchor: 'preseason-size-curves-kpis', titleKey: 'preseason.guide.sizeCurves.tour.figures.title', bodyKey: 'preseason.guide.sizeCurves.tour.figures.body' },
      { anchor: 'preseason-size-curves-chart', titleKey: 'preseason.guide.sizeCurves.tour.chart.title', bodyKey: 'preseason.guide.sizeCurves.tour.chart.body' },
      { anchor: 'preseason-size-curves-table', titleKey: 'preseason.guide.sizeCurves.tour.table.title', bodyKey: 'preseason.guide.sizeCurves.tour.table.body' },
      { anchor: 'preseason-size-curves-traceability', titleKey: 'preseason.guide.sizeCurves.tour.trace.title', bodyKey: 'preseason.guide.sizeCurves.tour.trace.body' },
    ],
  },
  'buy': {
    purposeKey: 'preseason.guide.buy.purpose',
    readingKey: 'preseason.guide.buy.reading',
    actionsKey: 'preseason.guide.buy.actions',
    statesKey: 'preseason.guide.buy.states',
    sourceKey: 'preseason.guide.buy.source',
    tour: [
      { anchor: 'page-status', titleKey: 'preseason.guide.buy.tour.state.title', bodyKey: 'preseason.guide.buy.tour.state.body' },
      { anchor: 'preseason-context', titleKey: 'preseason.guide.buy.tour.context.title', bodyKey: 'preseason.guide.buy.tour.context.body' },
      { anchor: 'preseason-buy-kpis', titleKey: 'preseason.guide.buy.tour.figures.title', bodyKey: 'preseason.guide.buy.tour.figures.body' },
      { anchor: 'preseason-buy-chart', titleKey: 'preseason.guide.buy.tour.chart.title', bodyKey: 'preseason.guide.buy.tour.chart.body' },
      { anchor: 'preseason-buy-lines', titleKey: 'preseason.guide.buy.tour.lines.title', bodyKey: 'preseason.guide.buy.tour.lines.body' },
      { anchor: 'preseason-buy-depth', titleKey: 'preseason.guide.buy.tour.depth.title', bodyKey: 'preseason.guide.buy.tour.depth.body' },
      { anchor: 'preseason-buy-week', titleKey: 'preseason.guide.buy.tour.week.title', bodyKey: 'preseason.guide.buy.tour.week.body' },
      { anchor: 'preseason-buy-scenarios', titleKey: 'preseason.guide.buy.tour.scenarios.title', bodyKey: 'preseason.guide.buy.tour.scenarios.body' },
      { anchor: 'preseason-buy-provenance', titleKey: 'preseason.guide.buy.tour.provenance.title', bodyKey: 'preseason.guide.buy.tour.provenance.body' },
    ],
  },
};
